import { useQuery } from '@apollo/client';
import React from 'react';
import { appPublicConfig } from '@/configs/appPublicConfig';
import { graphql } from '@/gql/__codegen__';
import type {
  PointHistoryEventFragment,
  PointHistoryUsage_PointHistoryQuery,
} from '@/gql/__codegen__/graphql';
import { dateFormatFromISOString } from '@/shared/helpers/formatter/dateFormatter';
import { groupByList } from '@/shared/helpers/groupByList';
import type { PointHistoryUsageLog } from '@/shared/libs/logger/__codegen__/pointHistoryUsage';
import { useSuperFlowClient } from '@/shared/libs/logger/useSuperFlowClient';

graphql(`
  fragment PointHistoryEvent on PointHistoryEvent {
    type
    operationDateTime
    operationTypeCode
    amount
    pointHistoryType
    itemType
    itemName
    itemDetail
  }
`);

const QUERY = graphql(`
  query pointHistoryUsage_pointHistory(
    $type: PointClassInput!
    $deviceType: String!
  ) {
    unextAccount {
      pointHistory(type: $type, deviceType: $deviceType) {
        events {
          ...PointHistoryEvent
        }
      }
    }
  }
`);

type State = {
  pageStatus: 'INIT' | 'PREPARED';
  historyList: [string, PointHistoryEventFragment[]][];
  canBeShownAdultContent: boolean;
};

type Action =
  | {
      type: 'INITIALIZED';
      payload: {
        pointHistoryQuery: PointHistoryUsage_PointHistoryQuery;
      };
    }
  | {
      type: 'TOGGLE_ADULT_CONTENT';
      payload: {
        canBeShownAdultContent: boolean;
      };
    };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'INITIALIZED': {
      const { pointHistoryQuery } = action.payload;

      const historyList = groupByList(
        pointHistoryQuery.unextAccount.pointHistory.events,
        (cur) => dateFormatFromISOString(cur.operationDateTime, 'yyyy年M月d日')
      );

      return {
        ...state,
        pageStatus: 'PREPARED',
        historyList,
      };
    }
    case 'TOGGLE_ADULT_CONTENT': {
      return {
        ...state,
        canBeShownAdultContent: action.payload.canBeShownAdultContent,
      };
    }
  }
};

export type UsePointHistoryUsage = {
  state: State;
  showAdultContent: () => void;
  maskAdultContent: () => void;
};

export function usePointHistoryUsage(): UsePointHistoryUsage {
  const [state, dispatch] = React.useReducer(reducer, {
    pageStatus: 'INIT',
    historyList: [],
    canBeShownAdultContent: false,
  });

  const { isReadyToSend, trackUserViewLog } =
    useSuperFlowClient(appPublicConfig);
  const isSendViewLogRef = React.useRef(false);

  React.useEffect(() => {
    if (isSendViewLogRef.current) return;
    if (!isReadyToSend) return;

    isSendViewLogRef.current = true;
    trackUserViewLog<PointHistoryUsageLog>({
      target: 'pointHistoryUsage',
      detail: {},
    });
  }, [isReadyToSend, trackUserViewLog]);

  useQuery(QUERY, {
    variables: {
      type: 'POINT',
      deviceType: '700',
    },
    onCompleted: (data) => {
      dispatch({
        type: 'INITIALIZED',
        payload: {
          pointHistoryQuery: data,
        },
      });
    },
  });

  const showAdultContent: UsePointHistoryUsage['showAdultContent'] =
    React.useCallback(() => {
      dispatch({
        type: 'TOGGLE_ADULT_CONTENT',
        payload: {
          canBeShownAdultContent: true,
        },
      });
    }, []);

  const maskAdultContent: UsePointHistoryUsage['maskAdultContent'] =
    React.useCallback(() => {
      dispatch({
        type: 'TOGGLE_ADULT_CONTENT',
        payload: {
          canBeShownAdultContent: false,
        },
      });
    }, []);

  return {
    state,
    showAdultContent,
    maskAdultContent,
  };
}
